<template>
  <Grid
    class="ukd-ingredients-grid"
    :pageable="pageable"
    :data-items="products"
    :skip="skip"
    :take="take"
    :total="totalProducts"
    :columns="columns"
    :loader="productsLoading"
    :filter="filter"
    :column-menu="!productsLoading"
    :sort="sort"
    :resizable="true"
    :reorderable="true"
    selected-field="selected"
    @columnresize="onColumnResize"
    @columnreorder="onColumnReorder"
    @filterchange="filterChangeHandler"
    @sortchange="sortChangeHandler"
    @pagechange="pageChangeHandler"
    @rowclick="changeSelection"
    @headerselectionchange="changeHeaderSelection"
    data-cy="ukd-ingredients-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e: any) => props.onSortchange(e)"
        @filterchange="(e: any) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:SelectCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <input
          type="checkbox"
          v-model="dataItem[field]"
          @change="emit('selectionChanged', [dataItem])"
        />
      </td>
    </template>
    <template v-slot:DetailsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <input
          class="fa fa-external-link details-icon"
          type="image"
          alt="Detail Icon"
          @click.prevent="openProductDetail(dataItem)"
          :src="detailIconUrl"
          data-cy="details-action"
        />
      </td>
    </template>
    <template v-slot:UkdIdCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <span v-if="!dataItem.DoNotDisplayUkdId">{{ dataItem[field] }}</span>
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridSelectionChangeEvent,
  type GridColumnProps,
  type GridColumnResizeEvent,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
  type GridRowClickEvent,
  type GridHeaderSelectionChangeEvent,
  type GridSortChangeEvent,
  type GridColumnReorderEvent,
} from "@progress/kendo-vue-grid";
import { ref, watchEffect } from "vue";
import detailIconUrl from "@/assets/img/DetailIcon.png";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { getFilterOperatorsForBoolean } from "@/shared/helpers/filtersHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import type { UkdProductWithSelection } from "@/api/scheduleA";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";

const notificationStore = useNotificationStore();

const props = defineProps<{
  products: UkdProductWithSelection[];
  productsLoading: boolean;
  pageSize: number;
  skip: number;
  totalProducts: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  canModifyCarts: boolean;
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<UkdProductWithSelection[]>;
}>();

const emit = defineEmits<{
  (e: "productDetailsClicked", product: UkdProductWithSelection): void;
  (e: "productsChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "selectionChanged", selectedIngredients: UkdProductWithSelection[]): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const gridName = "ukdIngredientsGrid";

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const columns = ref<GridColumnPropsExtended[]>([
  {
    field: "selected",
    cell: "SelectCell",
    width: "50px",
    sortable: false,
    filterable: false,
    headerClassName: "hide-menu",
    hidden: !props.canModifyCarts,
    hideInColumnMenu: true,
  },
  {
    title: "Details",
    cell: "DetailsCell",
    headerClassName: "hide-header",
    sortable: false,
    filterable: false,
    width: "40px",
    hideInColumnMenu: true,
  },
  {
    title: "Label Name",
    field: "LabelName",
    columnMenu: "columnMenu",
    hideInColumnMenu: true,
  },
  {
    title: "Label Number",
    field: "LabelNum",
    columnMenu: "columnMenu",
  },
  {
    title: "Company",
    field: "CompanyName",
    columnMenu: "columnMenu",
  },
  {
    title: "Brand Name",
    field: "BrandName",
    columnMenu: "columnMenu",
  },
  {
    title: "DPM Status",
    field: "Dpm",
    columnMenu: "columnMenu",
    onlyValueFilter: true,
  },
  {
    title: "Comment",
    field: "Comments",
    columnMenu: "columnMenu",
  },
  {
    title: "C/I",
    field: "CI",
    columnMenu: "columnMenu",
  },
  {
    title: "Passover",
    field: "Pesach",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForBoolean(),
  },
  {
    title: "UKD ID",
    field: "VueFullUkdId",
    cell: "UkdIdCell",
    columnMenu: "columnMenu",
  },
  {
    title: "Agency",
    field: "AgencyName",
    columnMenu: "columnMenu",
  },
]);

let pageSizes = [10, 20, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

GridOptionsService.loadGridOptions(gridName, columns.value);

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("productsChanged", {
    skip: 0,
    take: take.value,
    filterEvent: e,
  });
};

const sortChangeHandler = function (e: GridSortChangeEvent) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("productsChanged", {
    skip: event.page.skip,
    take: event.page.take,
    filterEvent: null,
  });
};

const changeSelection = function (
  event: GridSelectionChangeEvent | GridRowClickEvent
) {
  // if clicked on the details icon or canModifyCarts is false,
  // don't change the selection
  if (!props.canModifyCarts) return;

  const className = event.event?.target?.className;
  if (className.includes && className.includes("details-icon")) return;

  const product = event.dataItem as UkdProductWithSelection;
  if (!product) return;
  product.selected = !product.selected;
  emit("selectionChanged", [product]);
};

const changeHeaderSelection = function (event: GridHeaderSelectionChangeEvent) {
  const checked = event.event.target.checked;
  props.products.forEach((ingredient) => {
    ingredient.selected = checked;
  });
  emit("selectionChanged", props.products);
};

const openProductDetail = function (product: UkdProductWithSelection) {
  emit("productDetailsClicked", product);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) {
    columns.value = columnsProps;
    GridOptionsService.saveVisibleColumns(gridName, columnsProps);
  }
  console.debug("ukdIngredientsGrid.vue, onColumnSubmit", columnsProps);

  emit("columnssubmit", columnsProps);
};

const onColumnResize = function (e: GridColumnResizeEvent) {
  const col = GridOptionsService.getResizedColumn(e, columns.value);
  if (col.title)
    GridOptionsService.saveColumnWidth(gridName, col.title, e.newWidth);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  GridOptionsService.setSelectDetailsColumnOrder(e);
  columns.value = e.columns;
  GridOptionsService.saveColumnsOrder(gridName, e.columns);
  emit("columnreorder", e);
};

const resetColumnSelection = function () {
  columns.value.forEach((col) => {
    col.hidden = col.defaultHiddenValue ?? false;
  });
  GridOptionsService.clearVisibleColumns(gridName);
};

const resetColumnSizes = function (showNotification = true) {
  GridOptionsService.clearColumnWidths(gridName);
  if (showNotification) {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message:
        "Column sizes reset successfully. Please refresh the page to see the changes.",
      duration: 5000,
    });
  }
};

const resetColumnOrder = function () {
  columns.value.forEach((col) => {
    col.orderIndex = undefined;
  });
  GridOptionsService.clearColumnsOrder(gridName);
};

const resetAllSettings = function () {
  resetColumnSelection();
  resetColumnSizes(false);
  resetColumnOrder();

  notificationStore.notify({
    type: NotificationTypeEnum.success,
    message:
      "All settings reset successfully. Please refresh the page to see the changes.",
    duration: 5000,
  });
};

const executeAction = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ResetColumnSelection:
      resetColumnSelection();
      break;
    case GridMenuActionType.ResetColumnSizes:
      resetColumnSizes();
      break;
    case GridMenuActionType.ResetColumnOrder:
      resetColumnOrder();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetAllSettings();
      break;
  }
};

defineExpose({
  executeAction,
});

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.ukd-ingredients-grid {
  min-height: 450px;
  height: 800px;

  .fa-external-link {
    cursor: pointer;
  }
}
.k-grid {
  .hide-header {
    .k-grid-header-menu {
      display: none;
    }
  }
  .hide-header {
    .k-column-title {
      display: none;
    }
  }
  td {
    a {
      color: #337ab7;
    }
  }
}
</style>
